import { createContext, useContext } from 'react';

import { BillingAccountV2, OrderTemplateDetail } from './api/types';

export type ShiftDraftV2 = {
  start_at: string;
  end_at: string;
  time_zone_offset: number;
  break_minutes?: number;
  quantity?: number;
};

export type JobDraftV2 = {
  address_id: number | null;
  contact_id: number | null;
  skill_id: number | null;
  uniform_id: number | null;
  quantity: number;
  job_type: 'GIG' | 'LTA';
  is_holiday: boolean;
  pay_rate: number;
  cost_rate: number;
  original_pay_rate: number;
  original_cost_rate: number;
  instructions: string;
  address_instructions: string;
  contact_instructions: string;
  uniform_instructions: string;
  mandatory_break_time: number;
  share_contact_number: boolean;
  all_shifts_same_worker: boolean;
  auto_accept_requests: boolean;
  certificates: number[];
  shifts: ShiftDraftV2[];
  staffing_strategy?: 'OPEN_JOB_POST' | 'SEND_INVITATIONS';
  invite_workers?: number[];
  hire_workers?: number[];
  // "Make a private job available to qualified workers if the selected
  // workers are unavailable" — corresponds to v1's PRIVATE + publish:true
  // semantics. The backend uses this to schedule a fallback PUBLIC
  // posting (+4h or +30m) after invites/hires complete, but only when
  // the job is still unfilled.
  auto_fill?: boolean;
};

export type OrderStateV2 = {
  billingAccount: BillingAccountV2 | null;
  orderType: 'GIG' | 'LTA' | null;
  sourceTemplateId: number | null;
  isPrefilled: boolean;
  verificationEnabled: boolean;
  // Mirrors legacy gating: only show the QR check-in toggle when the
  // customer has `verification_enabled` turned on at the account level.
  verificationVisible: boolean;
  step: number;
  jobs: JobDraftV2[];
};

export type OrderActionsV2 = {
  setBillingAccount: (account: BillingAccountV2 | null) => void;
  setOrderType: (orderType: 'GIG' | 'LTA' | null) => void;
  applyTemplate: (template: OrderTemplateDetail) => void;
  clearTemplate: () => void;
  setStep: (step: number) => void;
  setJobs: (jobs: JobDraftV2[]) => void;
  setVerificationEnabled: (enabled: boolean) => void;
  setVerificationVisible: (visible: boolean, defaultEnabled: boolean) => void;
};

export const defaultJobDraftV2 = (orderType: 'GIG' | 'LTA'): JobDraftV2 => ({
  address_id: null,
  contact_id: null,
  skill_id: null,
  uniform_id: null,
  quantity: 1,
  job_type: orderType,
  is_holiday: false,
  pay_rate: 0,
  cost_rate: 0,
  original_pay_rate: 0,
  original_cost_rate: 0,
  instructions: '',
  address_instructions: '',
  contact_instructions: '',
  uniform_instructions: '',
  mandatory_break_time: 30,
  share_contact_number: false,
  all_shifts_same_worker: false,
  auto_accept_requests: false,
  certificates: [],
  shifts: [],
  staffing_strategy: orderType === 'GIG' ? 'OPEN_JOB_POST' : undefined,
  invite_workers: [],
  hire_workers: [],
  auto_fill: false,
});

export const initialOrderStateV2: OrderStateV2 = {
  billingAccount: null,
  orderType: null,
  sourceTemplateId: null,
  isPrefilled: false,
  verificationEnabled: false,
  verificationVisible: false,
  step: 0,
  jobs: [],
};

export const OrderStateContext =
  createContext<OrderStateV2>(initialOrderStateV2);
export const OrderActionsContext = createContext<OrderActionsV2 | null>(null);

export const useOrderStateV2 = () => useContext(OrderStateContext);

export const useOrderActionsV2 = () => {
  const value = useContext(OrderActionsContext);
  if (!value) {
    throw new Error(
      'useOrderActionsV2 must be used within OrderActionsContext',
    );
  }
  return value;
};
